<template>
  <div class="protection-page">
    <main class="protection-card" aria-labelledby="privacy-title">
      <p class="protection-eyebrow">Legal</p>
      <h1 id="privacy-title" class="protection-title">Website Privacy Policy</h1>

      <p class="protection-lede">
        Park Avenue Concierge Medicine (PACM MyChart) is sensitive to protecting the
        privacy of our website visitors. The purpose of this Privacy Statement is to let
        you know how PACM MyChart handles information received from you through this
        website, as well as inform you of the risks involved when you communicate or
        provide information by way of the Internet. Keep in mind that changes are
        periodically made to this Privacy Statement without notice and it is your
        responsibility to review the Statement when you use this website. By continuing to
        use this site, you consent to any changes to our policy or terms of use.
      </p>
      <p>
        During your visit to the PACM MyChart website, remember that the medical and
        health information presented here is intended to be general in nature, and should
        not be viewed as a substitute for professional advice. Please consult with a
        healthcare professional for all matters relating to personal medical and health
        care issues.
      </p>

      <section aria-labelledby="privacy-health-info">
        <h2 id="privacy-health-info" class="protection-heading">Health and Medical Information</h2>
        <p>
          Certain health and medical information about you is protected under the Health
          Insurance Portability and Accountability Act ("HIPAA") and applicable state law.
          This information may be provided by you online or offline, or may be collected
          by us from other methods such as through a health care provider. PACM MyChart
          protects covered health and medical information as required by HIPAA and
          applicable state law. Similarly, PACM MyChart may use covered health and medical
          information as permitted by HIPAA and applicable state law.
        </p>
        <p>
          In the event of any conflict between PACM MyChart HIPAA Notice of Privacy
          Practices and this Website Privacy Statement, the terms of our HIPAA Notice of
          Privacy Practices shall control.
        </p>
      </section>

      <section aria-labelledby="privacy-security">
        <h2 id="privacy-security" class="protection-heading">Security/Confidentiality</h2>
        <p>
          No transmission of data over the Internet or any wireless network can be
          guaranteed to be 100 percent secure. PACM MyChart will, however, use a number of
          physical security methods (such as locks and alarm systems); electronic security
          methods (passwords and encryption methods); and procedural methods (rules
          regarding the handling and use of information) to protect the security and
          integrity of information submitted through this Web site.
        </p>
      </section>

      <section aria-labelledby="privacy-collection">
        <h2 id="privacy-collection" class="protection-heading">Information Collection and Storage</h2>
        <p>
          Personally Identifiable Information: PACM MyChart does not collect information
          that personally identifies you unless you choose to provide it to PACM MyChart
          (for example, Web pages that allow you to register for a class, complete an
          online job application or send an e-mail message). The personal information you
          submit is shared only with those people at PACM MyChart who need this
          information to respond to your question or request, and as otherwise permitted
          by law to allow PACM MyChart to operate our business or to comply with
          applicable laws.
        </p>
        <p>
          As used in this Privacy Statement, personal information does not include
          information protected under HIPAA or applicable state laws, which is protected
          as described in the section "Health and Medical Information" above.
        </p>
        <p>
          Non-Personally Identifiable Information: PACM MyChart collects information that
          does not personally identify you for various purposes (for example, website
          usage, traffic patterns, site performance and related statistics based on your
          visits to our website).
        </p>
        <p>
          PACM MyChart collects some of this information using persistent "cookies" to
          recognize a repeat visitor and gather general information about how the site is
          viewed. PACM MyChart also collects and logs the IP address of each visitor to
          gather aggregate data on visitors to our site. Keep in mind that neither cookies
          nor IP addresses are used to collect any personal information from you.
        </p>
      </section>

      <section aria-labelledby="privacy-email">
        <h2 id="privacy-email" class="protection-heading">Email</h2>
        <p>
          Most email does not provide a completely secure and confidential means of
          communication. It is possible that your email could be viewed inappropriately by
          another Internet user. If you choose to provide us with your email, any
          misaddressing may result in others intercepting, altering, forwarding or using
          the information without authorization. If you wish to keep your information
          completely private, you should not use email.
        </p>
        <p>
          At times, a representative from PACM MyChart's website may wish to deliver
          information to you via email. In addition, PACM MyChart's Marketing and
          Communications Department and/or PACM MyChart Foundation may occasionally
          distribute news and announcements via email. Please note, PACM MyChart does not
          sell, trade, or otherwise share our email lists with others. You may remove your
          name from any list you have registered for using our website's established
          method.
        </p>
      </section>

      <section aria-labelledby="privacy-links">
        <h2 id="privacy-links" class="protection-heading">Links to Other Websites</h2>
        <p>
          PACM MyChart's website contains links to other websites that are not managed or
          controlled by PACM MyChart. These links are provided for your convenience only.
          PACM MyChart does not review, endorse or take responsibility for the content of
          these other websites. PACM MyChart bears no responsibility for the content in
          any linked websites - whether PACM MyChart provides a link or a third party
          links to PACM MyChart's website. If you decide to access any of the third party
          sites linked to PACM MyChart's website, you do this entirely at your own risk.
          It is up to you to take precautions to ensure that the third party you link to
          for your use is free of computer viruses, worms, trojan horses and other items
          of a destructive nature. If you choose to access such sites, you are subject to
          the privacy policies of those sites.
        </p>
      </section>

      <section aria-labelledby="privacy-children">
        <h2 id="privacy-children" class="protection-heading">Information from Children</h2>
        <p>
          PACM MyChart's website is not intended to attract or collect information from
          children under the age of 13, unless they receive verifiable parental consent.
          If you think PACM MyChart has collected personal information from a child under
          the age of 13 through this website, please contact PACM MyChart immediately.
        </p>
      </section>

      <section aria-labelledby="privacy-printing">
        <h2 id="privacy-printing" class="protection-heading">Printing or Downloading Information</h2>
        <p>
          Unless otherwise stated, you may print or download information from PACM
          MyChart's website for personal, noncommercial use only. You must, however,
          identify the source of the material and include a statement that the materials
          are protected by copyright law.
        </p>
      </section>

      <section aria-labelledby="privacy-contact">
        <h2 id="privacy-contact" class="protection-heading">Website Contact Information</h2>
        <p>
          If you have questions about this Privacy Statement, please contact PACM MyChart
          at:
          <a href="mailto:mail@pacmny.com">mail@pacmny.com</a>
        </p>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: "privacy-policy",
};
</script>

<style scoped lang="scss">
$color-ink: #17233d;
$color-body: #33415c;
$color-accent: #0060ff;
$color-border: #e2e8f0;

.protection-page {
  min-height: 100vh;
  width: 100%;
  padding: 3rem 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(160deg, #eef3fb 0%, #f7f9fc 55%, #ffffff 100%);
}

.protection-card {
  width: 100%;
  max-width: 46rem;
  background: #ffffff;
  border: 1px solid $color-border;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(15, 30, 60, 0.08);
  padding: 2.75rem clamp(1.5rem, 5vw, 3.5rem);
  color: $color-body;
  line-height: 1.65;
  font-size: 1.05rem;
}

.protection-eyebrow {
  margin: 0 0 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.8rem;
  font-weight: 700;
  color: $color-accent;
}

.protection-title {
  margin: 0 0 1.25rem;
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  font-weight: 700;
  color: $color-ink;
  letter-spacing: -0.01em;
}

.protection-lede {
  font-size: 1.1rem;
  color: $color-ink;
}

.protection-heading {
  margin: 2rem 0 1rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: $color-ink;
  padding-top: 1.5rem;
  border-top: 1px solid $color-border;
}

p {
  margin: 0 0 1.1rem;
}

a {
  color: $color-accent;
  font-weight: 600;
  text-decoration: underline;

  &:hover,
  &:focus-visible {
    text-decoration-thickness: 2px;
  }

  &:focus-visible {
    outline: 2px solid $color-accent;
    outline-offset: 2px;
  }
}

@media (max-width: 40rem) {
  .protection-page {
    padding: 1.5rem 0.75rem;
  }

  .protection-card {
    padding: 2rem 1.25rem;
    font-size: 1rem;
  }
}
</style>
